import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { AlertTriangle, Archive, ArchiveRestore, Download, Pencil, Plus, Printer, Receipt, TrendingDown, Trash2, Wallet } from 'lucide-react';
import {
  Button,
  DataTable,
  KPICard,
  PageHeader,
  StatusBadge,
  ViewSwitcher,
} from '@/components/ui';
import type { StatusKey, ViewMode } from '@/types';
import {
  type DepenseDTO,
  type DepenseFilters,
  type DepenseStats,
  type DepenseStatut,
  DEPENSE_CATEGORIES,
  DEPENSE_STATUTS,
  MODES_PAIEMENT,
  deleteDepense,
  getDepenseStats,
  listDepenses,
  openDepensePdf,
  openEtatDepensesPdf,
  updateDepense,
} from '@/services/depenses';
import { listChantiers } from '@/services/chantiers';
import { listTiers } from '@/services/tiers';
import { formatDZD, formatDZDCompact } from '@/utils/algeria';
import { toast } from '@/store/toastStore';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import { CATEGORIE_LABEL, MODE_PAIEMENT_LABEL } from './labels';
import { SaisieDepenseModal } from './SaisieDepenseModal';

const DONUT_COLORS = ['#1a1a2e', '#FFCD11', '#0073EA', '#00C875', '#F97316', '#64748b', '#E2445C', '#94a3b8'];
const STATUT_UI: Record<DepenseStatut, { label: string; status: StatusKey }> = {
  BROUILLON: { label: 'Brouillon', status: 'pending' },
  VALIDEE: { label: 'Validée', status: 'info' },
  PAYEE: { label: 'Payée', status: 'done' },
};
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const selectCls = 'h-10 rounded-md border border-border bg-surface-secondary px-3 text-sm';

export function DepensesPage() {
  const perms = usePerms('depenses');
  const queryClient = useQueryClient();
  const [view, setView] = useState<ViewMode>('table');
  const [modalOpen, setModalOpen] = useState(false);
  const [editDepense, setEditDepense] = useState<DepenseDTO | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [filtres, setFiltres] = useState<DepenseFilters>({ comptabilisation: 'toutes' });

  const setF = (patch: Partial<DepenseFilters>) => setFiltres((f) => ({ ...f, ...patch }));

  const { data, isLoading } = useQuery({
    queryKey: ['depenses', filtres, showArchived],
    queryFn: () => listDepenses({ ...filtres, archives: showArchived, size: 200 }),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) => updateDepense(id, { is_archived: archived }),
    onSuccess: (_d, v) => { queryClient.invalidateQueries({ queryKey: ['depenses'] }); toast.success(v.archived ? 'Dépense archivée.' : 'Dépense restaurée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDepense(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['depenses'] }); toast.success('Dépense supprimée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const { data: stats } = useQuery({ queryKey: ['depenses-stats'], queryFn: () => getDepenseStats() });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers', 'actifs'], queryFn: () => listChantiers() });
  const { data: tiers } = useQuery({ queryKey: ['tiers', 'all'], queryFn: () => listTiers({ size: 200 }) });

  const tiersMap = useMemo(() => {
    const m = new Map<string, string>();
    tiers?.items.forEach((t) => m.set(t.id, t.raison_sociale));
    return m;
  }, [tiers]);
  const fournisseursST = useMemo(
    () => (tiers?.items ?? []).filter((t) => t.type !== 'CLIENT'),
    [tiers],
  );

  const moisDelta = useMemo(() => {
    if (!stats || !stats.total_mois_precedent) return undefined;
    return ((stats.total_mois - stats.total_mois_precedent) / stats.total_mois_precedent) * 100;
  }, [stats]);

  const imprimer = async (mode: 'open' | 'download') => {
    try {
      await openEtatDepensesPdf(filtres, mode);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const columns: ColumnDef<DepenseDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Référence', size: 96 },
    { accessorKey: 'date_depense', header: 'Date', size: 88 },
    { accessorKey: 'libelle', header: 'Libellé', size: 200 },
    {
      accessorKey: 'numero_facture',
      header: 'N° facture',
      size: 92,
      cell: ({ getValue }) => <span className="text-xs text-content-secondary">{(getValue() as string) || '—'}</span>,
    },
    {
      accessorKey: 'tiers_id',
      header: 'Tiers',
      size: 136,
      cell: ({ getValue }) => {
        const id = getValue() as string | null;
        return <span className="text-xs text-content-secondary">{id ? tiersMap.get(id) ?? '—' : '—'}</span>;
      },
    },
    {
      accessorKey: 'categorie',
      header: 'Catégorie',
      size: 112,
      cell: ({ getValue }) => <span className="text-xs font-medium text-content-secondary">{CATEGORIE_LABEL[getValue() as DepenseDTO['categorie']]}</span>,
    },
    {
      accessorKey: 'montant_ttc',
      header: 'Montant TTC',
      size: 122,
      cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span>,
    },
    {
      accessorKey: 'statut',
      header: 'Statut',
      size: 98,
      cell: ({ getValue }) => {
        const s = STATUT_UI[getValue() as DepenseStatut];
        return <StatusBadge status={s.status} label={s.label} />;
      },
    },
    {
      accessorKey: 'comptabilisee',
      header: 'Justification',
      size: 128,
      cell: ({ getValue }) =>
        getValue() ? <StatusBadge status="done" label="Justifiée déd." /> : <StatusBadge status="pending" label="Réelle non déd." />,
    },
    {
      id: 'actions',
      header: '',
      size: 172,
      cell: ({ row }) => {
        const d = row.original;
        return (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDepensePdf(d.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Imprimer la dépense"><Printer className="size-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDepensePdf(d.id, 'download').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Télécharger la dépense"><Download className="size-4" /></Button>
            {showArchived ? (
              perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => archiveMutation.mutate({ id: d.id, archived: false })} aria-label="Restaurer"><ArchiveRestore className="size-4" /></Button>
            ) : (
              <>
                {perms.canModifier && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditDepense(d)} aria-label="Modifier"><Pencil className="size-4" /></Button>}
                {perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archiveMutation.mutate({ id: d.id, archived: true })} aria-label="Archiver"><Archive className="size-4" /></Button>}
              </>
            )}
            {perms.canSupprimer && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm(`Supprimer la dépense ${d.reference} ?`)) deleteMutation.mutate(d.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Dépenses"
        subtitle="Saisie, suivi et affectation analytique des dépenses"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Dépenses' }]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ViewSwitcher value={view} onChange={setView} views={['table', 'dashboard']} />
            <Button variant="secondary" onClick={() => imprimer('open')}>
              <Printer /> Imprimer l'état
            </Button>
            <Button variant="secondary" onClick={() => imprimer('download')}>
              <Download /> Télécharger
            </Button>
            {perms.canAjouter && (
              <Button onClick={() => setModalOpen(true)}>
                <Plus /> Nouvelle dépense
              </Button>
            )}
          </div>
        }
      />

      {view === 'dashboard' ? (
        <DepensesDashboard stats={stats} moisDelta={moisDelta} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <select value={filtres.categorie ?? ''} onChange={(e) => setF({ categorie: (e.target.value || undefined) as never })} className={selectCls}>
              <option value="">Toutes catégories</option>
              {DEPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORIE_LABEL[c]}</option>)}
            </select>
            <select value={filtres.chantier_id ?? ''} onChange={(e) => setF({ chantier_id: e.target.value || undefined })} className={selectCls}>
              <option value="">Tous chantiers</option>
              {chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}
            </select>
            <select value={filtres.tiers_id ?? ''} onChange={(e) => setF({ tiers_id: e.target.value || undefined })} className={selectCls}>
              <option value="">Fournisseur / sous-traitant</option>
              {fournisseursST.map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}
            </select>
            <select value={filtres.mode_paiement ?? ''} onChange={(e) => setF({ mode_paiement: (e.target.value || undefined) as never })} className={selectCls}>
              <option value="">Tous paiements</option>
              {MODES_PAIEMENT.map((m) => <option key={m} value={m}>{MODE_PAIEMENT_LABEL[m]}</option>)}
            </select>
            <select value={filtres.statut ?? ''} onChange={(e) => setF({ statut: (e.target.value || undefined) as never })} className={selectCls}>
              <option value="">Tous statuts</option>
              {DEPENSE_STATUTS.map((s) => <option key={s} value={s}>{STATUT_UI[s].label}</option>)}
            </select>
            <select value={filtres.mois ?? ''} onChange={(e) => setF({ mois: e.target.value ? Number(e.target.value) : undefined })} className={selectCls}>
              <option value="">Tous les mois</option>
              {MOIS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select value={filtres.comptabilisation ?? 'toutes'} onChange={(e) => setF({ comptabilisation: e.target.value as DepenseFilters['comptabilisation'] })} className={selectCls}>
              <option value="toutes">Toutes</option>
              <option value="comptabilisees">Justifiées et déductibles</option>
              <option value="non_comptabilisees">Réelles non déductibles</option>
            </select>
            <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
              <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Afficher les archivées
            </label>
          </div>

          <DataTable
            columns={columns}
            data={data?.items ?? []}
            loading={isLoading}
            pageSize={15}
            exportFileName="depenses"
            emptyMessage="Aucune dépense. Cliquez sur « Nouvelle dépense » pour commencer."
          />
        </div>
      )}

      <SaisieDepenseModal open={modalOpen} onOpenChange={setModalOpen} />
      <SaisieDepenseModal open={!!editDepense} depense={editDepense} onOpenChange={(o) => { if (!o) setEditDepense(null); }} />
    </>
  );
}

function DepensesDashboard({ stats, moisDelta }: { stats: DepenseStats | undefined; moisDelta: number | undefined }) {
  const donut = (stats?.par_categorie ?? []).map((c, i) => ({
    name: CATEGORIE_LABEL[c.categorie as keyof typeof CATEGORIE_LABEL] ?? c.categorie,
    value: c.total,
    color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard title="Total du mois" value={formatDZDCompact(stats?.total_mois ?? 0)} icon={Wallet} changePct={moisDelta} changeLabel="vs mois préc." accent="#1a1a2e" />
        <KPICard title="Dépenses chantiers" value={formatDZDCompact(stats?.total_chantiers ?? 0)} icon={Receipt} accent="#64748b" />
        <KPICard title="Frais généraux" value={formatDZDCompact(stats?.total_centres_cout ?? 0)} icon={Receipt} accent="#F97316" />
        <KPICard
          title="Réel non déductible"
          value={formatDZDCompact(stats?.total_non_comptabilise ?? 0)}
          icon={AlertTriangle}
          changePct={stats?.pct_non_comptabilise}
          changeLabel="du total annuel (gestion)"
          accent="#F97316"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
          <h2 className="mb-4 text-base font-bold text-content-primary">Répartition par catégorie (année)</h2>
          {donut.length === 0 ? (
            <p className="py-12 text-center text-sm text-content-muted">Aucune donnée.</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donut} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {donut.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatDZD(v)} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <TrendingDown className="size-4 text-status-stuck" />
              <h2 className="text-base font-bold text-content-primary">Pilotage interne</h2>
            </div>
            <p className="mt-2 text-sm text-content-secondary">
              <span className="text-2xl font-bold tabular-nums text-status-working">{(stats?.pct_non_comptabilise ?? 0).toFixed(1)} %</span>{' '}
              des dépenses de l'année sont réelles non déductibles : suivies en gestion (marge réelle des chantiers), hors G50 et résultat fiscal.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
